import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { API_URL, secret } from "../../constants";
import { logout, updateTokens } from "../auth-slice";
import { handleApiError } from "../../errors";
import { APP_CONFIG } from "../../config";
const baseQuery = fetchBaseQuery({
  baseUrl: APP_CONFIG.API_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.accessToken;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});
// Create a wrapper around the base query to handle token refresh
const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  console.log("😴😴 Response status:", result?.error?.status);

  // If we get a 401 Unauthorized response, try to refresh the token
  if (result?.error?.status === 401) {
    const refreshToken = api.getState().auth.refreshToken;
    console.log("😴😴 Refresh token:", refreshToken);

    if (refreshToken) {
      try {
        // Attempt to refresh the token
        const refreshResult = await baseQuery(
          {
            url: "/refresh-token",
            method: "POST",
            body: { refreshToken },
          },
          api,
          extraOptions
        );
        if ("error" in refreshResult) {
          handleApiError(refreshResult.error, {
            showToast: true, // Show toast notification
            onAuthError: () => {
              console.warn("⚠️ Authentication error detected, logging out...");
              api.dispatch(logout());
            },
          });
        }
        if (refreshResult?.data) {
          console.log("✅ Token refreshed successfully.");
          // Update tokens in store
          api.dispatch(
            updateTokens({
              accessToken: refreshResult.data.accessToken,
              refreshToken: refreshResult.data.refreshToken,
            })
          );

          // Retry the original request with the new token
          result = await baseQuery(args, api, extraOptions);
        } else {
          console.warn("⚠️ Token refresh failed, logging out...");
          api.dispatch(logout());
        }
      } catch (error) {
        console.error("❌ Token refresh error:", error);

        // Handle API errors properly
        handleApiError(error, {
          showToast: true, // Show toast notification
          onAuthError: () => {
            console.warn("⚠️ Authentication error detected, logging out...");
            api.dispatch(logout());
          },
        });
      }
    } else {
      console.warn("⚠️ No refresh token found, logging out...");
      api.dispatch(logout());
    }
  }

  return result;
};

export const publicApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getUserTransaction: builder.query({
      query: () => "/user-transactions",
    }),
    login: builder.mutation({
      query: (data) => ({
        url: "/login",
        method: "POST",
        body: data,
      }),
    }),
    changePassword: builder.mutation({
      query: (data) => ({
        url: "/changepassword",
        method: "POST",
        body: data,
      }),
    }),
    deleteAccount: builder.mutation({
      query: (data) => ({
        url: "/delete",
        method: "POST",
        body: data,
      }),
    }),
    initiatePasswordReset: builder.mutation({
      query: (data) => ({
        url: "/password-reset/initiate",
        method: "POST",
        body: data,
      }),
    }),
    verifyOtp: builder.mutation({
      query: (data) => ({
        url: "/password-reset/verify-otp",
        method: "POST",
        body: data,
      }),
    }),
    completePasswordReset: builder.mutation({
      query: (data) => ({
        url: "/password-reset/complete",
        method: "POST",
        body: data,
      }),
    }),
    getGames: builder.query({
      query: () => "/betgenius/displaydata",
      // query: () => "/api/events",
    }),
    // getGames: builder.query({
    //   query: () => "/api/getgames",
    // }),

    getMatchById: builder.query({
      query: (id) => `/events/${id}`,
    }),
    getTopLeagues: builder.query({
      query: () => `/top-leagues`,
    }),
    getCountries: builder.query({
      query: () => `/betgenius/getcountrieswithleagues`,
    }),
    register: builder.mutation({
      query: (data) => ({
        url: "/register",
        method: "POST",
        body: data,
      }),
    }),
    // getGameLaunchUrl
    gameLaunchUrl: builder.mutation({
      query: (data) => ({
        url: "/api/mobuleapi/launch",
        method: "POST",
        body: data,
      }),
    }),
    // Expanse provider launch
    expanseGameLaunchUrl: builder.mutation({
      query: (data) => ({
        url: "/api/expanse/launch-game",
        method: "POST",
        body: data,
      }),
    }),
    // smartsoft provider launch
    smartsoftGameLaunchUrl: builder.mutation({
      query: (data) => ({
        url: "/smartsoft/geturl",
        method: "POST",
        body: data,
      }),
    }),
    // SA Gaming provider launch
    saGameLaunchUrl: builder.mutation({
      query: (data) => ({
        url: "/sa/launchurl",
        method: "POST",
        body: data,
      }),
    }),
    // Tada provider launch
    tadaGameLaunchUrl: builder.mutation({
      query: (data) => ({
        url: "/tada/loginwithoutredirect",
        method: "POST",
        body: data,
      }),
    }),
    // deposit
    withdraw: builder.mutation({
      query: (data) => ({
        url: "/api/jambobet/withdraw",
        method: "POST",
        body: data,
      }),
    }),
    // withdraw
    deposit: builder.mutation({
      query: (data) => ({
        url: "/api/jambobet/deposit",
        method: "POST",
        body: data,
      }),
    }),
    // get transaction
    getTransactions: builder.mutation({
      query: ({ limit, status, page, ...data }) => {
        const queryParams = new URLSearchParams();

        if (limit) queryParams.append("limit", limit);
        if (status) queryParams.append("status", status);
        if (page) queryParams.append("page", page);

        return {
          url: `/api/jambobet/transactions${
            queryParams.toString() ? `?${queryParams.toString()}` : ""
          }`,
          method: "POST",
          body: data,
        };
      },
    }),
    refreshToken: builder.mutation({
      query: (refreshData) => ({
        url: "/refresh-token",
        method: "POST",
        body: refreshData,
      }),
    }),
    // Create sports bet
    createSportsBet: builder.mutation({
      query: (data) => ({
        url: "/api/jambobet/bets",
        method: "POST",
        body: data,
      }),
    }),
    // Get user bets
    getUserBets: builder.mutation({
      query: (data) => ({
        url: "/api/jambobet/bets/user",
        method: "POST",
        body: data,
      }),
    }),
    // Get pragmatic games
    getPragmaticGames: builder.query({
      query: () => "/api/pragmaticgameiconurls",
    }),
    // Get pragmatic games
    pragmaticLaunchUrl: builder.mutation({
      query: (data) => ({
        url: "/pragmatic/geturl",
        method: "POST",
        body: data,
      }),
    }),
    // Get euro virtual games
    getEuroVirtualGames: builder.query({
      query: () => "/fetch-games",
    }),
    // Get euro virtual games
    euroVirtualLaunchUrl: builder.mutation({
      query: (data) => ({
        url: "/api/launch-game",
        method: "POST",
        body: data,
      }),
    }),
    // Get aviatrix launch url
    aviatrixLaunch: builder.mutation({
      query: (data) => ({
        url: "/aviatrix/launch-game",
        method: "POST",
        body: data,
      }),
    }),
    // Get fazi launch url
    faziLaunch: builder.mutation({
      query: (data) => ({
        url: "/api/fazi/launch",
        method: "POST",
        body: data,
      }),
    }),
    // Get velligames launch url
    veligamesLaunch: builder.mutation({
      query: (data) => ({
        url: "/veligames/launch-game",
        method: "POST",
        body: data,
      }),
    }),
    // Get leprecoins launch url
    leprecoinsLaunch: builder.mutation({
      query: (data) => ({
        url: "/api/leprecoins/launch-game",
        method: "POST",
        body: data,
      }),
    }),
    // Get aviatrix launch url
    airtelDeposit: builder.mutation({
      query: (data) => ({
        url: "/payment/airteldeposit",
        method: "POST",
        body: data,
      }),
    }),
    // Get mpesa deposit
    mpesaDesposit: builder.mutation({
      query: (data) => ({
        url: "/payment/mpesadeposit",
        method: "POST",
        body: data,
      }),
    }),
    // Get mpesa withdraw push
    mpesaWithdraw: builder.mutation({
      query: (data) => ({
        url: "/payment/mpesadisburse",
        method: "POST",
        body: data,
      }),
    }),
    // Get games caategoris
    categoryGames: builder.mutation({
      query: (data) => ({
        url: "/category/games",
        method: "POST",
        body: data,
      }),
    }),
    // Get categories
    getCategories: builder.query({
      query: () => "/categories",
    }),
    getRecentWinners: builder.query({
      query: () => "/api/jambobet/casino/bets",
    }),
  }),
});

export const {
  useVeligamesLaunchMutation,
  useSaGameLaunchUrlMutation,
  useSmartsoftGameLaunchUrlMutation,
  useGetCountriesQuery,
  useGetTopLeaguesQuery,
  useLeprecoinsLaunchMutation,
  useMpesaWithdrawMutation,
  useTadaGameLaunchUrlMutation,
  useExpanseGameLaunchUrlMutation,
  useCategoryGamesMutation,
  useGetCategoriesQuery,
  useCompletePasswordResetMutation,
  useInitiatePasswordResetMutation,
  useVerifyOtpMutation,
  useChangePasswordMutation,
  useDeleteAccountMutation,
  useGetRecentWinnersQuery,
  useAirtelDepositMutation,
  useMpesaDespositMutation,
  usePragmaticLaunchUrlMutation,
  useGetPragmaticGamesQuery,
  useFaziLaunchMutation,
  useAviatrixLaunchMutation,
  useEuroVirtualLaunchUrlMutation,
  useGetEuroVirtualGamesQuery,
  useRefreshTokenMutation,
  useGetTransactionsMutation,
  useGetMatchByIdQuery,
  useGetUserBetsMutation,
  useCreateSportsBetMutation,
  useWithdrawMutation,
  useDepositMutation,
  useGameLaunchUrlMutation,
  useGetGamesQuery,
  useGetUserTransactionQuery,
  useLoginMutation,
  useRegisterMutation,
} = publicApi;
